import { useState, type FormEvent } from "react";
import { Check, Copy } from "lucide-react";
import type { DnsRecord, DomainProblem, Workspace } from "../api/contract";
import type { PublishingController } from "../hooks/usePublishing";
import { displayOrigin, liveStatus } from "../lib/workspaceState";
import { Address } from "./PublishFlow";
import { Skeleton, SkeletonGroup } from "./Skeleton";
import { Spinner } from "./Spinner";
import { AreaError } from "./area";

/** Shape of the address/status card, while it loads. */
function PublishingSkeleton() {
  return (
    <SkeletonGroup label="Loading your publishing status" className="bw-library bw-publishing">
      <section>
        <Skeleton width={120} height={13} radius={4} />
        <div style={{ margin: "10px 0" }}><Skeleton width={180} height={14} radius={4} /></div>
        <Skeleton width={110} height={36} radius={6} />
      </section>
      <section>
        <Skeleton width={100} height={13} radius={4} />
        <div style={{ margin: "10px 0" }}><Skeleton width="60%" height={20} radius={4} /></div>
      </section>
    </SkeletonGroup>
  );
}

/** What the publishing groups of Site settings show until the status has loaded: a skeleton, or the way to retry. */
export function PublishingPending({ publishing }: { publishing: PublishingController }) {
  return publishing.failed
    ? <AreaError inline message="Your address and plan couldn't be loaded." onRetry={publishing.retry} />
    : <PublishingSkeleton />;
}

const DOMAIN_PROBLEMS: Record<DomainProblem, string> = {
  invalid: "That doesn’t look like a domain. Try something like www.yourbusiness.sg.",
  platform: "That’s a Stack.je address. Use a domain you own.",
  taken: "That domain is already connected to another website.",
  plan: "Custom domains come with the Pro plan.",
  exists: "Your website already has a domain. Remove it first.",
  "in-use": "Your website uses this domain as its address. Switch back first.",
  "not-ready": "Your domain isn’t verified yet.",
  none: "There’s no domain connected.",
  failed: "That didn’t work. Try again in a moment.",
};

function ago(iso: string | null): string {
  if (!iso) return "Not checked yet.";
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  return minutes < 1 ? "Checked just now." : minutes < 60 ? `Checked ${minutes} min ago.` : `Checked ${Math.round(minutes / 60)} h ago.`;
}

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="bw-btn bw-btn--quiet bw-copy"
      onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true), () => setCopied(false))}
      aria-label={`Copy ${label}`}
    >
      {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Records({ records }: { records: DnsRecord[] }) {
  return (
    <ul className="bw-records">
      {records.map((record) => (
        <li key={`${record.type}-${record.name}`}>
          <dl>
            <dt>Type</dt><dd>{record.type}</dd>
            <dt>Name</dt><dd><code>{record.name}</code> <CopyValue label={`${record.type} name`} value={record.name} /></dd>
            <dt>Value</dt><dd><code>{record.value}</code> <CopyValue label={`${record.type} value`} value={record.value} /></dd>
          </dl>
        </li>
      ))}
    </ul>
  );
}

interface DomainViewProps {
  workspace: Workspace;
  publishing: PublishingController;
  onAskAdmin: (text: string) => void;
  onAddressChanged: () => void;
}

/** Where the website lives: its Stack.je address, and connecting the owner's own domain. */
export function DomainView({ workspace, publishing, onAskAdmin, onAddressChanged }: DomainViewProps) {
  const status = publishing.status;
  const [hostname, setHostname] = useState("");
  const live = liveStatus(workspace);
  if (!status) return <PublishingPending publishing={publishing} />;
  const domain = status.domain ?? null;
  const platformAddress = displayOrigin(status.origin);
  const problem = publishing.domainProblem ? <p className="bw-item-note" role="alert">{DOMAIN_PROBLEMS[publishing.domainProblem]}</p> : null;

  const connect = async (event: FormEvent) => {
    event.preventDefault();
    if (hostname.trim() && await publishing.addDomain(hostname.trim())) setHostname("");
  };

  return (
    <div className="bw-library bw-publishing" aria-busy={publishing.domainBusy !== null || publishing.savingAddress}>
      <section aria-label="Your Stack.je address">
        {/* Always the Stack.je address: Change picks a different one. The owner's own domain has its section below. */}
        <Address publishing={publishing} current={platformAddress} onChanged={onAddressChanged} />
        {domain?.inUse && <p className="bw-item-note">Your own domain is the main address. This one keeps working too.</p>}
      </section>

      {status.domains && (
        <section aria-labelledby="bw-pub-domain">
          <h3 id="bw-pub-domain" className="bw-library-title bw-settings-h3">Your own domain{!status.entitled && <span className="bw-settings-chip" data-plan="pro">Pro</span>}</h3>
          {!status.entitled ? (
            <div className="bw-plan">
              <p>Use your own domain, like yourbusiness.sg, with the Pro plan. Your Stack.je address keeps working.</p>
              <div className="bw-actions">
                <button type="button" className="bw-btn bw-btn--primary" onClick={() => void publishing.subscribe("monthly")} disabled={publishing.checkout === "starting"} aria-busy={publishing.checkout === "starting"}>{publishing.checkout === "starting" && <Spinner />}Get the Pro plan</button>
              </div>
            </div>
          ) : !domain ? (
            <form className="bw-domain-form" onSubmit={(event) => void connect(event)}>
              <label htmlFor="bw-domain" className="bw-sr">Domain you own</label>
              <input id="bw-domain" value={hostname} placeholder="www.yourbusiness.sg" autoCapitalize="none" spellCheck={false} inputMode="url"
                onChange={(event) => setHostname(event.target.value)} />
              <p className="bw-item-note">Use the www address. Your Stack.je address keeps working the whole time.</p>
              {problem}
              <div className="bw-actions">
                <button type="submit" className="bw-btn bw-btn--primary" disabled={!hostname.trim() || publishing.domainBusy !== null}>
                  {publishing.domainBusy === "adding" && <Spinner />}
                  {publishing.domainBusy === "adding" ? "Connecting…" : "Connect domain"}
                </button>
              </div>
            </form>
          ) : domain.status !== "active" ? (
            <ol className="bw-domain-steps">
              <li data-state="done"><strong>Domain added</strong><span>{domain.hostname}</span></li>
              <li data-state="active">
                <strong>Add {domain.records.length === 1 ? "this record" : "these records"} at your domain provider</strong>
                <span>Some providers call Name “Host”.</span>
                <Records records={domain.records} />
              </li>
              <li data-state="active">
                <strong>{domain.status === "failed" ? "Your domain needs attention" : "Waiting for your domain"}</strong>
                <span>{domain.issue ? `${domain.issue} ` : ""}{ago(domain.checkedAt)} It can take up to a day.</span>
                {problem}
                <div className="bw-actions">
                  <button type="button" className="bw-btn bw-btn--quiet" onClick={() => void publishing.checkDomain()} disabled={publishing.domainBusy !== null}>
                    {publishing.domainBusy === "checking" && <Spinner />}
                    {publishing.domainBusy === "checking" ? "Checking…" : "Check again"}
                  </button>
                  <button type="button" className="bw-btn bw-btn--text" onClick={() => onAskAdmin(`Help me connect my domain ${domain.hostname}`)}>Ask Stax for help</button>
                </div>
              </li>
              <li data-state="next"><strong>Switch to your domain</strong><span>Once it’s verified, you confirm the switch. Your Stack.je address keeps working the whole time.</span></li>
            </ol>
          ) : domain.inUse ? (
            <>
              <p>Your website’s address is <strong>{domain.hostname}</strong>{live.kind === "never-published" || live.kind === "has-changes" ? " once you publish" : ""}. Your Stack.je address keeps working.</p>
              {problem}
              <div className="bw-actions">
                <button type="button" className="bw-btn bw-btn--quiet" onClick={() => void publishing.switchAddress("platform").then((ok) => ok && onAddressChanged())} disabled={publishing.domainBusy !== null}>
                  {publishing.domainBusy === "switching" && <Spinner />}
                  Go back to the Stack.je address
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bw-callout" data-tone="done" role="status"><strong>{domain.hostname} is ready</strong><p>Make it your website’s address, then publish to switch.</p></div>
              {problem}
              <div className="bw-actions">
                <button type="button" className="bw-btn bw-btn--primary" onClick={() => void publishing.switchAddress("domain").then((ok) => ok && onAddressChanged())} disabled={publishing.domainBusy !== null}>
                  {publishing.domainBusy === "switching" && <Spinner />}
                  {publishing.domainBusy === "switching" ? "Switching…" : "Use it as my address"}
                </button>
              </div>
            </>
          )}
          {domain && !domain.inUse && (
            <button type="button" className="bw-btn bw-btn--text" onClick={() => void publishing.removeDomain()} disabled={publishing.domainBusy !== null}>
              {publishing.domainBusy === "removing" && <Spinner />}
              {publishing.domainBusy === "removing" ? "Removing…" : "Remove this domain"}
            </button>
          )}
        </section>
      )}

    </div>
  );
}
